// Primitives de focus du DS — port de `gdRefocus` / `GD_FOCUSABLE` (ui_kits/goldorac/core/
// primitives.jsx). Le README amont (§ NAVIGATION CLAVIER) en fait une règle : « une seule
// implémentation, à consommer, jamais à recopier » — le sélecteur de focusables était déjà écrit
// deux fois (piège de focus des modales et sortie de menu portalé), et deux listes qui divergent,
// c'est l'un des deux comportements qui casse en silence.

export const DS_FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Focus différé d'une frame : au moment où l'on ferme une surface (menu, modale), l'élément visé
// peut encore être masqué par elle, ou pas encore remonté. `isConnected` évite de rendre le focus à
// un élément que le rendu vient de détacher — le navigateur le renverrait sur `<body>`, et la
// tabulation reprendrait au début du document.
export function dsRefocus (el: HTMLElement | null | undefined): void {
  if (!el || !import.meta.client) { return; }
  requestAnimationFrame(() => {
    if (el.isConnected) { el.focus(); }
  });
}

export function dsRestoreFocus (el: HTMLElement | null | undefined): void {
  if (!el || !import.meta.client) { return; }
  requestAnimationFrame(() => {
    const active = el.ownerDocument.activeElement;
    const lost = !active || active === el.ownerDocument.body || !active.isConnected;
    if (el.isConnected && lost) { el.focus(); }
  });
}

// Éléments réellement atteignables au clavier dans `root` : `offsetParent` écarte ce qui est masqué
// (`display: none`, ancêtre replié), qu'un sélecteur CSS ne sait pas distinguer.
export function dsFocusables (root: HTMLElement | Document | null): HTMLElement[] {
  if (!root) { return []; }
  return [...root.querySelectorAll<HTMLElement>(DS_FOCUSABLE)].filter(el => el.offsetParent !== null);
}

// Voisin de `from` dans l'ordre de tabulation du document, en ignorant les overlays téléportés
// (`exclude`) : le menu d'un select vit en fin de `body`, donc l'ordre DOM depuis son champ de
// recherche ne mène pas au champ suivant du formulaire. Il faut repartir du déclencheur.
export function dsSiblingFocusable (
  from: HTMLElement | null,
  options: { back?: boolean; exclude?: string } = {},
): HTMLElement | null {
  if (!from) { return null; }
  const doc = from.ownerDocument ?? document;
  const items = dsFocusables(doc)
    .filter(el => !(options.exclude && el.closest(options.exclude)));
  const at = items.indexOf(from);
  if (at === -1) { return null; }
  return items[at + (options.back ? -1 : 1)] ?? null;
}

// The removed row is still on screen, leaving, when the removal is confirmed: the focus goes to the
// row after it, else the one before, else `fallback`. Deferred past the trap that gives the focus
// back to the trigger, which sits in the leaving row.
export function dsFocusAfterRemoval (row: HTMLElement | null, rows: string, fallback: HTMLElement | null = null): void {
  const others = row?.parentElement
    ? [...row.parentElement.children].filter((el): el is HTMLElement => el !== row && el.matches(rows))
    : [];
  const next = others.find(el => Boolean(row!.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) ?? others.at(-1);
  void nextTick(() => dsRefocus((next ? dsFocusables(next)[0] : null) ?? fallback));
}
